import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ButtonGroup,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import TransfersFilterDialog from "../../components/Inventory/TransfersFilterDialog";
import MoveItemDialog from "../../components/Inventory/MoveItemDialog";
import ReceiveItemDialog from "../../components/Inventory/ReceiveItemDialog";
import { searchStyles } from "../../styles/formStyles";
import { getTransfersWithFilters, getAllBuildings } from "../../services/transfer";

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  In_Transit: { bg: "rgba(255, 149, 0, 0.10)", color: "#F39600" },
  Picked: { bg: "rgba(74, 85, 101, 0.10)", color: "#61708A" },
  In_Building: { bg: "rgba(138, 43, 226, 0.10)", color: "#8A2BE2" },
  In_Room: { bg: "rgba(0, 94, 255, 0.10)", color: "#005EFF" },
  Transfered: { bg: "rgba(0, 178, 33, 0.1)", color: "#00B221" },
};

const Transfers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(7);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<{ statuses: string[]; types: string[] }>({ statuses: [], types: [] });
  const [openMove, setOpenMove] = useState(false);
  const [openReceive, setOpenReceive] = useState(false);
  const [buildings, setBuildings] = useState<any[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (searchParams.get("action") === "move") {
      setOpenMove(true);
      setSearchParams({});
    } else if (searchParams.get("action") === "receive") {
      setOpenReceive(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadBuildings = async () => {
      try {
        const data = await getAllBuildings();
        setBuildings(Array.isArray(data) ? data : []);
      } catch {
        setBuildings([]);
      }
    };
    loadBuildings();
  }, []);

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTransfersWithFilters({
        searchTerm: debouncedSearch || undefined,
        buildingId: selectedBuilding ? Number(selectedBuilding) : undefined,
        statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
        transferTypes: filters.types.length > 0 ? filters.types : undefined,
        page: page - 1,
        size: pageSize,
      });

      if (response && typeof response === "object") {
        const items = response.data || response.content || response || [];
        setRows(Array.isArray(items) ? items : []);
        setTotalPages(response.totalPages || Math.ceil((response.totalElements || 0) / pageSize));
        setTotalElements(response.totalElements || 0);
      } else {
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch {
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedBuilding, filters.statuses, filters.types, page, pageSize]);

  useEffect(() => {
    loadTransfers();
  }, [loadTransfers]);

  const filterCount = filters.statuses.length + filters.types.length;
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);

  return (
    <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", p: 1, gap: 2 }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <Box display="flex" gap={2}>
            <TextField
              placeholder="Search by SKU, Name..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "grey.500" }} />
                  </InputAdornment>
                ),
              }}
              sx={searchStyles}
            />
            <TextField
              select
              size="small"
              value={selectedBuilding}
              onChange={(e) => { setSelectedBuilding(e.target.value); setPage(1); }}
              SelectProps={{ displayEmpty: true }}
              sx={{ width: "15rem", "& .MuiOutlinedInput-root": { height: "40px", borderRadius: "12px", "&:hover fieldset": { borderColor: "rgba(74, 85, 101, 0.10)" }, "&.Mui-focused fieldset": { borderColor: "rgba(74, 85, 101, 0.10)" } }, "& .MuiInputLabel-root": { color: "grey.700" }, "& .MuiSelect-select": { color: "grey.700" } }}
            >
              <MenuItem value="">All</MenuItem>
              {buildings.map((b: any) => (
                <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              startIcon={<FilterAltOutlinedIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ borderRadius: "6px", border: "1px solid rgba(74, 85, 101, 0.10)", backgroundColor: "#FFF", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", textTransform: "none", color: "text.primary", px: 2, "&:hover": { backgroundColor: "#FFF" } }}
            >
              Filter{" "}
              {filterCount > 0 && (
                <Typography component="span" sx={{ ml: 0.5, color: "primary.main" }}>
                  ({filterCount})
                </Typography>
              )}
            </Button>
            <ButtonGroup variant="contained" sx={{ height: "2.5rem", borderRadius: "0.6rem" }}>
              <Button onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ textTransform: "none", px: "1rem", fontWeight: 500, fontSize: "0.9rem", "&:hover": { backgroundColor: "primary.main" } }}>
                Transfer
              </Button>
              <Button onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ minWidth: "2.5rem", px: "0.5rem", "&:hover": { backgroundColor: "primary.main" } }}>
                <KeyboardArrowDownIcon />
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { width: "12rem", borderRadius: "0.8rem", boxShadow: 3, mt: "0.5rem" } }}
        >
          <MenuItem onClick={() => { setMenuAnchor(null); setOpenMove(true); }}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Move" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); setOpenReceive(true); }}>
            <ListItemIcon><LoginIcon sx={{ transform: "scaleX(-1)" }} /></ListItemIcon>
            <ListItemText primary="Receive" />
          </MenuItem>
        </Menu>

        <Box sx={{ p: 1, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Paper sx={{ maxHeight: "500px", overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <Table stickyHeader  sx={{ minWidth: "600px" }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>SKU</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Barcode Number</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Item</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1, whiteSpace: "nowrap" }}>Transfer Type</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1, whiteSpace: "nowrap" }}>Performed By</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Origin Location</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Destination Location</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row: any) => (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.inventoryId}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.barcode || "..."}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.itemName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.transferType || "..."}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.performedByName || "..."}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1, maxWidth: "200px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, overflow: "hidden" }}>
                          <Typography noWrap sx={{ fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                            {row.originLocation || "..."}
                          </Typography>
                          {row.originLocation && (
                            <Tooltip
                              title={row.originLocation}
                              open={openTooltipId === `${row.id}-origin`}
                              onClose={() => setOpenTooltipId(null)}
                              disableHoverListener
                              disableFocusListener
                              arrow
                            >
                              <ErrorOutlineOutlinedIcon
                                onMouseEnter={() => setOpenTooltipId(`${row.id}-origin`)}
                                onMouseLeave={() => setOpenTooltipId(null)}
                                onClick={(e) => { e.stopPropagation(); setOpenTooltipId(openTooltipId === `${row.id}-origin` ? null : `${row.id}-origin`); }}
                                sx={{ fontSize: "1rem", color: "grey.500",transform: "scaleY(-1)", flexShrink: 0, cursor: "pointer" }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1, maxWidth: "200px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, overflow: "hidden" }}>
                          <Typography noWrap sx={{ fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                            {row.destinationLocation || "..."}
                          </Typography>
                          {row.destinationLocation && (
                            <Tooltip
                              title={row.destinationLocation}
                              open={openTooltipId === `${row.id}-dest`}
                              onClose={() => setOpenTooltipId(null)}
                              disableHoverListener
                              disableFocusListener
                              arrow
                            >
                              <ErrorOutlineOutlinedIcon
                                onMouseEnter={() => setOpenTooltipId(`${row.id}-dest`)}
                                onMouseLeave={() => setOpenTooltipId(null)}
                                onClick={(e) => { e.stopPropagation(); setOpenTooltipId(openTooltipId === `${row.id}-dest` ? null : `${row.id}-dest`); }}
                                sx={{ fontSize: "1rem", color: "grey.500",transform: "scaleY(-1)", flexShrink: 0, cursor: "pointer" }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Chip
                          label={row.transferStatus?.replace(/_/g, " ")}
                          sx={{
                            backgroundColor: STATUS_COLORS[row.transferStatus]?.bg || "grey.light",
                            color: STATUS_COLORS[row.transferStatus]?.color || "grey.primary",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            height: "1.75rem",
                            minWidth: "100px",
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Button
                          startIcon={<VisibilityOutlinedIcon />}
                          onClick={() => navigate(`/inventory/transfers/view?id=${row.inventoryId || row.id}`)}
                          sx={{ color: "grey.700", textTransform: "none", fontSize: "0.8rem", "&:hover": { backgroundColor: "transparent" } }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {totalElements > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}

      <MoveItemDialog open={openMove} onClose={() => setOpenMove(false)} onSuccess={loadTransfers} />
      <ReceiveItemDialog open={openReceive} onClose={() => setOpenReceive(false)} onSuccess={loadTransfers} />

      <TransfersFilterDialog
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        onApply={(newFilters: any) => {
          setFilters(newFilters);
          setFilterAnchor(null);
          setPage(1);
        }}
      />
    </Box>
  );
};
export default Transfers;
